// src/components/wordbook/WordbookShow.tsx (라이브러리 버튼 부분 수정)
import {Fragment, FC, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
    Grid,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Divider,
    IconButton,
    Checkbox,
} from '@mui/material';
import {
    ArrowBack,
    Edit,
    Delete,
    Visibility as VisibilityIcon,
    VisibilityOff,
    FolderOpen,
    AutoAwesome,
    DeleteForever,
    Restore,
    LibraryBooks,
    LibraryAdd,
} from '@mui/icons-material';
import {useWordbook, useDeleteWordbook, useToggleWordbookVisibility} from '../../hooks/useWordbook';
import {useDeleteWord, usePermanentDeleteWord, useRestoreWord} from '../../hooks/useWord';
import {Visibility} from '../../types/wordbook';
import {AiAddWordsDialog} from './AiAddWordsDialog';
import {EditWordDialog} from './EditWordDialog';
import {WordBulkActions} from './WordBulkActions';
import { formatKstDateTime } from '../../utils/dateTime';
import {MagicCardStatusChip} from './MagicCardStatusChip';
import {MagicCardSummaryBadges} from './MagicCardSummaryBadges';
import {MagicCardExpandedPanel} from './MagicCardExpandedPanel';
import {deriveMagicCardDisplayStatus} from '../../utils/magicCardSummary';
import {DictionaryInfoPanel} from './DictionaryInfoPanel';

export const WordbookShow: FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const wordbookId = parseInt(id || '0');

    const {data: wordbook, isLoading, error} = useWordbook(wordbookId);
    const deleteWordbook = useDeleteWordbook();
    const toggleVisibility = useToggleWordbookVisibility();
    const deleteWord = useDeleteWord();
    const permanentDeleteWord = usePermanentDeleteWord();
    const restoreWord = useRestoreWord();

    // 다이얼로그 상태
    const [aiAddOpen, setAiAddOpen] = useState(false);
    const [editWordOpen, setEditWordOpen] = useState(false);
    const [selectedWordId, setSelectedWordId] = useState<number | null>(null);

    // 일괄 선택 상태
    const [selectedWordIds, setSelectedWordIds] = useState<number[]>([]);

    const [expandedWordId, setExpandedWordId] = useState<number | null>(null);
    const [expandedDictWordId, setExpandedDictWordId] = useState<number | null>(null);

    const handleDelete = () => {
        if (confirm('단어장을 삭제하시겠습니까?')) {
            deleteWordbook.mutate(wordbookId, {
                onSuccess: () => {
                    navigate('/wordbook');
                },
            });
        }
    };

    const handleToggleVisibility = () => {
        toggleVisibility.mutate(wordbookId);
    };

    const handleDeleteWord = (wordId: number) => {
        if (confirm('단어를 삭제하시겠습니까?')) {
            deleteWord.mutate({wordId, wordbookId});
        }
    };

    const handlePermanentDeleteWord = (wordId: number) => {
        if (confirm('⚠️ 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            permanentDeleteWord.mutate({wordId, wordbookId});
        }
    };

    const handleRestoreWord = (wordId: number) => {
        restoreWord.mutate({wordId, wordbookId});
    };

    const handleEditWord = (wordId: number) => {
        setSelectedWordId(wordId);
        setEditWordOpen(true);
    };

    // 일괄 선택 핸들러
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedWordIds(wordbook?.words?.filter(w => !w.deletedAt).map(w => w.id) || []);
        } else {
            setSelectedWordIds([]);
        }
    };

    const handleSelectOne = (wordId: number, checked: boolean) => {
        if (checked) {
            setSelectedWordIds([...selectedWordIds, wordId]);
        } else {
            setSelectedWordIds(selectedWordIds.filter(id => id !== wordId));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error || !wordbook) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error?.message || '단어장을 찾을 수 없습니다.'}
                </Alert>
            </Box>
        );
    }

    const activeWords = wordbook.words?.filter(w => !w.deletedAt) ?? [];
    const allSelected = activeWords.length > 0 && selectedWordIds.length === activeWords.length;
    const someSelected = selectedWordIds.length > 0 && selectedWordIds.length < activeWords.length;

    return (
        <Box p={3}>
            {/* 헤더 */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Button
                        startIcon={<ArrowBack/>}
                        onClick={() => navigate('/wordbook')}
                    >
                        목록으로
                    </Button>
                    <Typography variant="h4">
                        {wordbook.title}
                    </Typography>
                    {wordbook.deletedAt && (
                        <Chip label="삭제됨" color="error"/>
                    )}
                    {wordbook.libraryId != null && (
                        <Chip
                            icon={<LibraryBooks/>}
                            label="라이브러리 등록됨"
                            color="secondary"
                        />
                    )}
                </Box>
                <Box display="flex" gap={1}>
                    {/* 라이브러리 버튼 - libraryId 유무에 따라 등록/수정 */}
                    {!wordbook.deletedAt && (
                        <>
                            {wordbook.libraryId != null ? (
                                // 이미 등록된 경우: 수정 버튼
                                <Button
                                    startIcon={<Edit/>}
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => navigate(`/library/${wordbook.libraryId}/edit`)}
                                >
                                    라이브러리 수정
                                </Button>
                            ) : (
                                // 미등록인 경우: 등록 버튼
                                <Button
                                    startIcon={<LibraryAdd/>}
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => navigate(`/library/create?wordbookId=${wordbookId}`)}
                                >
                                    라이브러리 등록
                                </Button>
                            )}
                        </>
                    )}
                    <Button
                        startIcon={wordbook.visibility === Visibility.VISIBLE ? <VisibilityIcon/> : <VisibilityOff/>}
                        onClick={handleToggleVisibility}
                        disabled={!!wordbook.deletedAt}
                    >
                        {wordbook.visibility === Visibility.VISIBLE ? '노출 중' : '숨김'}
                    </Button>
                    <Button
                        startIcon={<Edit/>}
                        variant="outlined"
                        onClick={() => navigate(`/wordbook/${wordbookId}/edit`)}
                        disabled={!!wordbook.deletedAt}
                    >
                        수정
                    </Button>
                    <Button
                        startIcon={<Delete/>}
                        variant="outlined"
                        color="error"
                        onClick={handleDelete}
                        disabled={!!wordbook.deletedAt}
                    >
                        삭제
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* 기본 정보 카드 */}
                <Grid size={{xs: 12, md: 6}}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                기본 정보
                            </Typography>
                            <Divider sx={{mb: 2}}/>
                            {wordbook.thumbnailUrl && (
                                <Box mb={2}>
                                    <img
                                        src={wordbook.thumbnailUrl}
                                        alt={wordbook.title}
                                        style={{
                                            width: '100%',
                                            maxWidth: 150,
                                            height: 'auto',
                                            borderRadius: 8,
                                        }}
                                    />
                                </Box>
                            )}
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        ID
                                    </Typography>
                                    <Typography variant="body1">{wordbook.id}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        제목
                                    </Typography>
                                    <Typography variant="body1">{wordbook.title}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        소유자
                                    </Typography>
                                    <Typography variant="body1">
                                        {wordbook.ownerName || '-'}
                                        {wordbook.ownerEmail && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {wordbook.ownerEmail}
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        그룹
                                    </Typography>
                                    <Box mt={0.5}>
                                        {wordbook.groupTitle ? (
                                            <Chip
                                                icon={<FolderOpen/>}
                                                label={wordbook.groupTitle}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="body1">-</Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        언어 쌍
                                    </Typography>
                                    <Typography variant="body1">
                                        {wordbook.headLang} → {wordbook.meaningLang}
                                    </Typography>
                                </Box>
                                {/* 라이브러리 ID 표시 */}
                                {wordbook.libraryId != null && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            라이브러리 ID
                                        </Typography>
                                        <Typography variant="body1">
                                            <Chip
                                                label={`#${wordbook.libraryId}`}
                                                size="small"
                                                color="secondary"
                                                onClick={() => navigate(`/library/${wordbook.libraryId}`)}
                                                clickable
                                            />
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 통계 정보 카드 */}
                <Grid size={{xs: 12, md: 6}}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                통계 정보
                            </Typography>
                            <Divider sx={{mb: 2}}/>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        총 단어 수
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        {wordbook.totalWords.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box display="flex" gap={1}>
                                    <Chip
                                        label={wordbook.visibility === Visibility.VISIBLE ? '노출' : '숨김'}
                                        color={wordbook.visibility === Visibility.VISIBLE ? 'success' : 'default'}
                                        size="small"
                                    />
                                    {wordbook.libraryId != null && (
                                        <Chip label="라이브러리" color="secondary" size="small"/>
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        생성일
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatKstDateTime(wordbook.createdAt)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        수정일
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatKstDateTime(wordbook.updatedAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 단어 목록 */}
                <Grid size={{xs: 12}}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    단어 목록
                                </Typography>
                                {!wordbook.deletedAt && (
                                    <Button
                                        variant="contained"
                                        startIcon={<AutoAwesome/>}
                                        onClick={() => setAiAddOpen(true)}
                                        size="small"
                                    >
                                        AI로 단어 추가
                                    </Button>
                                )}
                            </Box>

                            {/* 일괄 작업 바 */}
                            {selectedWordIds.length > 0 && (
                                <Box mb={2}>
                                    <WordBulkActions
                                        selectedWordIds={selectedWordIds}
                                        wordbookId={wordbookId}
                                        onClearSelection={() => setSelectedWordIds([])}
                                    />
                                </Box>
                            )}

                            <MagicCardSummaryBadges words={wordbook.words ?? []} />
                            <Divider sx={{mb: 2}}/>
                            {wordbook.words?.length === 0 ? (
                                <Typography color="text.secondary" textAlign="center" py={4}>
                                    등록된 단어가 없습니다.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell>순서</TableCell>
                                            <TableCell>단어</TableCell>
                                            <TableCell>의미</TableCell>
                                            <TableCell>매직카드</TableCell>
                                            <TableCell>난이도</TableCell>
                                            <TableCell>액션</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {wordbook.words?.map((word) => {
                                            const expanded = expandedWordId === word.id;
                                            const dictExpanded = expandedDictWordId === word.id;
                                            const display = deriveMagicCardDisplayStatus(word.magicCard);
                                            const anyExpanded = expanded || dictExpanded;
                                            return (
                                                <Fragment key={word.id}>
                                                    <TableRow
                                                        sx={{
                                                            opacity: word.deletedAt ? 0.5 : 1,
                                                            backgroundColor: word.deletedAt ? '#ffebee' : 'inherit',
                                                            '& > *': {borderBottom: anyExpanded ? 'unset' : undefined},
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            {!word.deletedAt && (
                                                                <Checkbox
                                                                    checked={selectedWordIds.includes(word.id)}
                                                                    onChange={(e) => handleSelectOne(word.id, e.target.checked)}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{word.sortOrder + 1}</TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {word.wordText}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {word.wordLang}
                                                                </Typography>
                                                                {word.phonetic && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{ fontFamily: 'monospace' }}
                                                                        color="text.secondary"
                                                                    >
                                                                        [{word.phonetic}]
                                                                    </Typography>
                                                                )}
                                                                {word.pos && (
                                                                    <Chip
                                                                        label={word.pos}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                                    />
                                                                )}
                                                                {word.dictId && (
                                                                    <Chip
                                                                        label={`dict #${word.dictId}`}
                                                                        size="small"
                                                                        color={dictExpanded ? 'primary' : 'default'}
                                                                        onClick={() =>
                                                                            setExpandedDictWordId(dictExpanded ? null : word.id)
                                                                        }
                                                                        sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {word.meaningText}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {word.meaningLang}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <MagicCardStatusChip
                                                                status={display.status}
                                                                caption={display.caption}
                                                                expanded={expanded}
                                                                onClick={() => setExpandedWordId(expanded ? null : word.id)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {word.difficultyBase && (
                                                                <Chip
                                                                    label={word.difficultyBase.toFixed(1)}
                                                                    size="small"
                                                                    color={
                                                                        word.difficultyBase < 30 ? 'success' :
                                                                            word.difficultyBase < 70 ? 'warning' : 'error'
                                                                    }
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {word.deletedAt ? (
                                                                <Box display="flex" gap={0.5}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleRestoreWord(word.id)}
                                                                        title="복원"
                                                                        color="success"
                                                                    >
                                                                        <Restore fontSize="small"/>
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handlePermanentDeleteWord(word.id)}
                                                                        title="영구 삭제"
                                                                        color="error"
                                                                    >
                                                                        <DeleteForever fontSize="small"/>
                                                                    </IconButton>
                                                                </Box>
                                                            ) : (
                                                                <Box display="flex" gap={0.5}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEditWord(word.id)}
                                                                        title="수정"
                                                                    >
                                                                        <Edit fontSize="small"/>
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteWord(word.id)}
                                                                        title="삭제"
                                                                        color="error"
                                                                    >
                                                                        <Delete fontSize="small"/>
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    {dictExpanded && word.dictionaryInfo && (
                                                        <TableRow>
                                                            <TableCell colSpan={7} sx={{bgcolor: 'primary.50', p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)'}}>
                                                                <DictionaryInfoPanel info={word.dictionaryInfo} />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    {dictExpanded && !word.dictionaryInfo && (
                                                        <TableRow>
                                                            <TableCell colSpan={7} sx={{bgcolor: 'grey.50', p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)'}}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    매칭된 사전 항목이 없습니다.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    {expanded && (
                                                        <TableRow>
                                                            <TableCell colSpan={7} sx={{bgcolor: 'grey.50', p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)'}}>
                                                                <MagicCardExpandedPanel word={word} />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* 다이얼로그들 */}
            <AiAddWordsDialog
                open={aiAddOpen}
                onClose={() => setAiAddOpen(false)}
                wordbookId={wordbookId}
                wordLang={wordbook.headLang}
                meaningLang={wordbook.meaningLang}
                existingWordTexts={(wordbook.words ?? []).map((w) => w.wordText)}
            />

            {selectedWordId && (
                <EditWordDialog
                    open={editWordOpen}
                    onClose={() => {
                        setEditWordOpen(false);
                        setSelectedWordId(null);
                    }}
                    wordId={selectedWordId}
                    wordbookId={wordbookId}
                    word={(wordbook.words ?? []).find(w => w.id === selectedWordId)!}
                />
            )}
        </Box>
    );
};
